import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, LogIn, LayoutDashboard } from "lucide-react";
import { SITE, telLink, waLink } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";

const links = [
  { href: "/#home", label: "Home" },
  { href: "/#services", label: "Services" },
  { href: "/#areas", label: "Areas" },
  { href: "/#why", label: "Why Us" },
  { href: "/#reviews", label: "Reviews" },
  { href: "/#faq", label: "FAQ" },
  { href: "/#contact", label: "Contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dashboardPath = user?.role === "admin" ? "/boxbquote" : "/dashboard";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/60" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#home" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="inline-flex h-10 w-10 rounded-2xl bg-[#0A192F] text-white font-display font-extrabold items-center justify-center text-xl">B</span>
          <span className="font-display font-bold text-[#0A192F] text-lg tracking-tight">
            BoxB<span className="text-[#2563EB]">.</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`}
               className="text-sm font-medium text-slate-700 hover:text-[#0A192F] transition-colors">
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href={telLink} data-testid="nav-call-btn"
             className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:text-[#2563EB] transition-colors">
            <Phone className="h-4 w-4" /> {SITE.phone}
          </a>
          {user ? (
            <Link to={dashboardPath} data-testid="nav-dashboard-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0F172A] transition-all hover:shadow-lg">
              <LayoutDashboard className="h-4 w-4" /> Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-signin-btn"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A192F] hover:text-[#2563EB] transition-colors">
                <LogIn className="h-4 w-4" /> Sign in
              </Link>
              <a href="#contact" data-testid="nav-quote-btn"
                 className="inline-flex items-center rounded-full bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0F172A] transition-all hover:shadow-lg">
                Get Free Quote
              </a>
            </>
          )}
        </div>

        <button data-testid="mobile-menu-toggle" aria-label="Toggle menu"
                onClick={() => setOpen(!open)} className="lg:hidden p-2 -mr-2 text-[#0A192F]">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 shadow-xl">
          <div className="px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                 data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                 className="px-3 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-slate-50">
                {l.label}
              </a>
            ))}
            {user ? (
              <Link to={dashboardPath} onClick={() => setOpen(false)}
                    className="mt-3 inline-flex justify-center rounded-full bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold">
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-signin-btn"
                      className="mt-3 inline-flex items-center justify-center gap-2 rounded-full border border-[#0A192F] text-[#0A192F] px-6 py-3 text-sm font-semibold">
                  <LogIn className="h-4 w-4" /> Sign in with Google
                </Link>
                <a href="#contact" onClick={() => setOpen(false)}
                   className="mt-2 inline-flex justify-center rounded-full bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold">
                  Get Free Quote
                </a>
              </>
            )}
            <a href={waLink()} target="_blank" rel="noopener noreferrer"
               className="mt-2 inline-flex justify-center rounded-full bg-[#25D366] text-white px-6 py-3 text-sm font-semibold">
              WhatsApp Us
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
